import React, { useState } from 'react';
import CoreSkeletonBox from 'src/shared/CoreSkeletonBox';
import styled from 'styled-components';
import {
	firstSectionWrapperClass,
	updatedTextClass,
	updatedTextWrapperClass,
} from '@components/report/style.css';
import { useTtReportGet } from 'src/apis/orval/endpoints/v2-report/v2-report';
import { useSearchParams } from 'react-router-dom';
import { AudienceAdapter } from '@components/report/section-content/common/audience/AudienceAdapter';
import { useReportPollingContext } from '@components/report/report-layout/ReportPollingContext';
import { CoreButton, CoreSelect } from '@featuring-corp/components';
import { Trans, useTranslation } from 'react-i18next';
import ReportSection from '@components/report/report-section/ReportSection';
import Audience from '@components/report/section-content/common/audience/Audience';
import ReportSubSection from '@components/report/report-sub-section/ReportSubSection';
import ReportIndex from '@components/report/report-index/ReportIndex';
import useScrollIntoView from '@components/report/useScrollntoView';
import SectionMessages from '@components/report/section-messages/SectionMessages';
import { KeyMetricsTtAdapter } from '@components/report/section-content/tt/key-metrics/KeyMetricsTtAdapter';
import { FollowerGrowthTtAdapter } from '@components/report/section-content/tt/follower-growth/FollowerGrowthAdapter';
import { EngagementTtAdapter } from '@components/report/section-content/tt/engagement/EngagementTtAdapter';
import { ContentTtAdapter } from '@components/report/section-content/tt/content/ContentTtAdapter';
import { VideoContentTtAdapter } from '@components/report/section-content/tt/video-content/VideoContentTtAdapter';
import { SimilarGroupTtAdapter } from '@components/report/section-content/tt/similar-group/SimilarGroupTtAdapter';
import KeyMetricsTt from '@components/report/section-content/tt/key-metrics/KeyMetrics';
import FollowerGrowthTt from '@components/report/section-content/tt/follower-growth/FollowerGrowth';
import EngagementTt from '@components/report/section-content/tt/engagement/EngagementTt';
import ContentTt from '@components/report/section-content/tt/content/ContentTt';
import VideoContentTt from '@components/report/section-content/tt/video-content/VideoContentTt';
import SimilarGroupTt, {
	SimilarGroupTtProps,
} from '@components/report/section-content/tt/similar-group/SimilarGroupTt';

const indexItems = [
	'핵심 지표',
	'팔로워 성장률',
	'오디언스',
	'인게이지먼트',
	'콘텐츠',
	'유사 그룹',
];

const TiktokContainer = () => {
	const { t } = useTranslation('infReportTiktok');
	const [search_params] = useSearchParams();
	const platformPk = search_params.get('pk') as string;
	const {
		isReportLoading,
		isUpdateLoading,
		needUpdate,
		readyForUpdate,
		isPollingFailed,
	} = useReportPollingContext();

	const { selectedSection, moveScroll } = useScrollIntoView('h2');

	const [sortVideoContentBy, setSortVideoContentBy] = useState('newest');

	const {
		data: {
			keyMetrics,
			followerGrowth,
			audience,
			engagement,
			content,
			videoContent,
			similarGroup,
		} = {},
		isLoading: isQueryLoading,
		isError: isQueryError,
		error,
		refetch,
	} = useTtReportGet(platformPk, {
		query: {
			select: (data) => ({
				keyMetrics: new KeyMetricsTtAdapter(data).adapt(),
				followerGrowth: new FollowerGrowthTtAdapter(data).adapt(),
				audience: new AudienceAdapter(data).adapt(),
				engagement: new EngagementTtAdapter(data).adapt(),
				content: new ContentTtAdapter(data).adapt(),
				videoContent: new VideoContentTtAdapter(data).adapt(
					sortVideoContentBy as 'newest' | 'views',
				),
				similarGroup: new SimilarGroupTtAdapter(data).adapt(),
			}),
			refetchOnWindowFocus: false,
			enabled: !!platformPk,
			retry: false,
		},
	});

	const isLoading = isQueryLoading || isReportLoading;
	// polling 이 완료 없이 종료된 경우(에러/시간 초과)도 에러 화면과 동일하게 처리
	const isError = isQueryError || isPollingFailed;

	return (
		<>
			<ReportIndex
				indexItems={indexItems}
				selectedSection={selectedSection ?? ''}
				handleMoveScroll={moveScroll}
			/>
			<ReportContentWrapper>
				{/* 섹션 메시지 */}
				<SectionMessages
					isError={isError}
					errorCode={(error as any)?.statusCode}
					isLoading={isLoading}
				/>

				<div className={firstSectionWrapperClass}>
					<div className={updatedTextWrapperClass}>
						<p className={updatedTextClass}>
							Last update:{' '}
							{isLoading ? (
								<CoreSkeletonBox _width="80px" _height="14px" radius="4px" />
							) : (
								keyMetrics?.updatedAt ?? ' - '
							)}
						</p>
						{needUpdate && (
							<CoreButton
								text={t('report.tiktok.tt-report.button.need-update')}
								buttonType="secondary"
								size="sm"
								isLoading={isUpdateLoading}
								disabled={!isUpdateLoading && !readyForUpdate}
								onClick={() => refetch()}
							/>
						)}
					</div>

					{/* 핵심 지표 */}
					<ReportSection
						title={t('report.tiktok.tt-report.item.key-metrics')}
						tooltip={t('report.tiktok.tt-report.report-section.key-metrics.tooltip')}
						description={t(
							'report.tiktok.tt-report.report-section.key-metrics.description',
						)}
					>
						<KeyMetricsTt
							isLoading={isLoading}
							isError={isError}
							{...keyMetrics?.props}
						/>
					</ReportSection>
				</div>

				{/* 팔로워 성장률 */}
				<ReportSection
					title={t('report.tiktok.tt-report.item.follower-growth')}
					description={t(
						'report.tiktok.tt-report.report-section.follower-growth.description',
					)}
				>
					<FollowerGrowthTt isLoading={isLoading} isError={isError} {...followerGrowth} />
				</ReportSection>

				{/* 오디언스 */}
				<ReportSection
					title={t('report.tiktok.tt-report.item.audience')}
					description={t('report.tiktok.tt-report.report-section.audience.description')}
				>
					<Audience
						platformCode="tt"
						{...audience}
						isLoading={isLoading}
						isError={isError}
					/>
				</ReportSection>

				{/* 인게이지먼트 */}
				<ReportSection
					title={t('report.tiktok.tt-report.item.engagement')}
					description={t('report.tiktok.tt-report.report-section.engagement.description')}
				>
					<EngagementTt isLoading={isLoading} isError={isError} {...engagement} />
				</ReportSection>

				{/* 콘텐츠 */}
				<ReportSection title={t('report.tiktok.tt-report.item.content')}>
					<ContentTt {...content} isLoading={isLoading} isError={isError} />
				</ReportSection>

				{/* 동영상 콘텐츠 */}
				<ReportSubSection
					title={t('report.tiktok.tt-report.report-subsection.video-content.title')}
					tooltip={t(
						'report.tiktok.tt-report.report-subsection.video-content.title-content.tooltip',
					)}
					titleContent={
						<CoreSelect
							size="sm"
							width={171}
							defaultValue={sortVideoContentBy}
							setValue={setSortVideoContentBy}
							dropdownZIndex={10000}
						>
							<CoreSelect.Item value="newest">{t('sort-content.newest')}</CoreSelect.Item>
							<CoreSelect.Item value="views">{t('sort-content.views')}</CoreSelect.Item>
						</CoreSelect>
					}
				>
					<VideoContentTt {...videoContent} isLoading={isLoading} isError={isError} />
				</ReportSubSection>

				{/* 유사 그룹 */}
				<ReportSection
					title={t('report.tiktok.tt-report.item.similar-group')}
					tooltip={t('report.tiktok.tt-report.report-section.similar-group.tooltip')}
					description={
						<>
							<Trans
								t={t}
								i18nKey="report.tiktok.tt-report.report-section.similar-group.description"
								components={[
									<span key={'user-name'}>{similarGroup?.desc?.userName ?? '-'}</span>,
									<span key={'category'}>{similarGroup?.desc?.category ?? '-'}</span>,
									<span key={'followers'}>
										{t('report.tiktok.tt-report.report-section.similar-group.followers', {
											followers: similarGroup?.desc?.followers ?? '-',
										})}
									</span>,
								]}
							/>
						</>
					}
				>
					<SimilarGroupTt
						{...(similarGroup?.props as SimilarGroupTtProps<string[]>)}
						isLoading={isLoading}
						isError={isError}
					/>
				</ReportSection>
			</ReportContentWrapper>
		</>
	);
};

const ReportContentWrapper = styled.div`
	overflow: hidden;
	height: auto;
	min-height: 100%;
	flex-grow: 1;
	flex-shrink: 0;
	padding: 60px 0 150px;

	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;

	gap: 80px;
`;

export default TiktokContainer;
